/**
 * VRG-545 — the "Potential subscription ARR" column.
 *
 * ## The name says ARR because the figure IS annual, and "revenue" understated it
 *
 * Renamed 2026-09-08 from "Potential subscription revenue". Nothing about the arithmetic
 * moved — it already annualised both halves (the graduated activation ladder × 12, plus
 * the plan's MONTHLY price × 12) — so the old label described a per-something figure the
 * column has never produced, and left a reader to work out the period from the tooltip.
 * ARR is the word the number already meant.
 *
 * ⚠️ It is still list-price arithmetic on a self-reported volume, not a booked figure.
 * "ARR" is a strong-sounding acronym and the tooltip does the work of saying so; do not
 * let the name talk anyone out of reading it.
 *
 * ## ⚠️ THIS IS A DERIVED COLUMN AND IT IS DELIBERATELY NOT IN `WEBSITE_FIELDS`
 *
 * Every other column on this grid is generated from that manifest, and the temptation is to
 * add a sixteenth entry and get the column for free. It would be wrong, and not merely
 * untidy: `WEBSITE_FIELDS` is THE CONTRACT WITH vera-website, and
 * `signups-website-field-coverage.guard.spec.ts` reads that repo's own `content/roi.ts` and
 * asserts the manifest against it. An entry for a figure vera-website has never heard of
 * would make the guard assert a field the site does not collect — so the guard either fails
 * on a correct manifest, or gets loosened to tolerate entries it cannot check, which is the
 * same as deleting the half that catches real drift.
 *
 * A forwarded field and a computed one are different claims. The manifest states the first.
 * This states the second, beside it. See working-conventions §75.
 *
 * ## The currency, which is NOT the row's
 *
 * ⚠️ THIS FIGURE IS IN VERA'S PRICING CURRENCY, NOT THE VISITOR'S. Every other money column
 * here takes its symbol from `roi_submission.currency`, because those figures are what the
 * visitor typed under a currency they chose. This one is not: it is built from Vera's own
 * catalog — plan `amount` and the Stripe metered price's `activation_tiers` — which carries
 * no currency column at all and is denominated in USD throughout.
 *
 * So labelling this with the ROW's currency would put `€657,588` on a euro lead's row while
 * the number is dollars, which is exactly the "wrong fact rendered confidently" failure that
 * `currencySymbolOf`'s own docblock is organised against. The resolver is still used — the
 * rule against a hardcoded `'$'` holds, and it is the shared symbol table that answers — it
 * is just asked about the catalog's currency rather than the submission's.
 *
 * The one thing genuinely lost is comparability against the same row's `price` / `cac`
 * columns on a non-USD lead. Stated in the tooltip rather than papered over with a
 * conversion nobody computed (VRG-577 is the conversion subsystem; nothing here converts).
 */
import React from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { EMPTY_VALUE, Tooltip } from '@repo/ui'

import { headerWithInfo, INFO_HEADER_ALLOWANCE } from 'components'
import type { SubscriptionPlan } from 'services/subscriptionApi'
import type { SignupEntry } from 'hooks/api/useSignups'

import { potentialSubscriptionRevenue, type PotentialRevenue } from './potentialRevenue'
import { currencySymbolOf } from './websiteValues'

/**
 * The catalog's currency.
 *
 * `stripe_subscription_products` has no currency column (checked — `amount` is a bare
 * integer of cents), so this is not readable from the data and is asserted here in one
 * place, named, rather than assumed at three call sites. If Vera ever prices a plan in
 * anything else, this is the line that has to move and the column that has to grow a
 * per-plan symbol.
 */
const CATALOG_CURRENCY = 'USD'

/**
 * The label, once. `headerName` and `renderHeader` both need it, and a column whose menu
 * entry says one thing while its rendered header says another is a rename half-done — the
 * column-visibility panel, the column menu and the CSV export all read `headerName`, none
 * of which render `renderHeader`.
 */
const COLUMN_LABEL = 'Potential subscription ARR'

/** Through the shared resolver, never a `'$'` literal — see this file's header. */
const catalogSymbol = () => currencySymbolOf({ currency: CATALOG_CURRENCY })

const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

/**
 * §49 — sized for the HEADER, and re-measured after the rename rather than scaled down by
 * the four characters "…ARR" is shorter than "…revenue". Lato is proportional and `ARR` is
 * three capitals where `revenue` was seven mostly-narrow lowercase letters, so a
 * character-count adjustment is not a width.
 *
 * ⚠️ MEASURED IN THE BROWSER on `/internal/signups` at 1280×800, by walking the rendered
 * header cell rather than by estimating. The decomposition, for a header that is both
 * `sortable` and carries an info glyph:
 *
 *     column                                      261
 *       cell padding            10 + 10  = 20
 *       title-container padding-left       8
 *       sort-icon container               28   ← present because `sortable: true`
 *       info glyph + its margin           18   ← INFO_HEADER_ALLOWANCE
 *       ────────────────────────────────────
 *       chrome                            74
 *       label text ("Potential subscription ARR", 14px Lato)   165
 *       minimum                                                239   → 22px slack
 *
 * ⚠️ NOTE THE CHROME IS 74, NOT THE 63 THE MANIFEST'S `width` DOC QUOTES. The extra is the
 * 28px sort-icon container, which a non-sortable column does not reserve. 63 remains a safe
 * FLOOR for the manifest's own columns; it is not the number to size a sortable+info header
 * against, and using it here would have left this one 11px short of its own label.
 */
const HEADER_WIDTH = 243

const money = (value: number): string =>
  `${catalogSymbol()}${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`

/**
 * What the cell says on hover — the arithmetic, shown rather than asserted.
 *
 * A single large number under a heading reading "potential revenue" is the most
 * misreadable thing on this page: it looks like a measurement and is a list-price
 * calculation on two figures a stranger typed. Breaking it into its two halves, naming the
 * volume it was priced on, and saying whose currency it is in, is what lets a reviewer
 * decide how much of it to believe.
 */
const breakdown = (r: PotentialRevenue): string => {
  const parts = [
    `${money(r.activationAnnual)} of activations a year, priced on the graduated schedule at ${Math.round(r.monthlyActivations).toLocaleString('en-US')} a month.`,
  ]
  if (r.plan) {
    parts.push(
      r.planSubstituted
        ? `Plus ${money(r.planAnnual)} of subscription. They asked about ${r.requestedTierKey}, which is contact-sales and has no price, so this is priced at ${r.plan.title} — an assumption, and a floor.`
        : `Plus ${money(r.planAnnual)} of ${r.plan.title} subscription.`,
    )
  } else {
    parts.push('They named no plan, so this is the activation half only.')
  }
  parts.push(
    `In ${CATALOG_CURRENCY} — Vera's own pricing, not the currency the visitor entered their figures in.`,
  )
  return parts.join(' ')
}

const HEADER_NOTE =
  'What this lead would be worth a year at list price: their stated annual volume priced through the graduated activation schedule, plus the annual price of the plan they said they were interested in. ' +
  'Built from a figure the visitor typed on the marketing site, so it is arithmetic on a self-reported number and not a forecast. ' +
  "Blank when the volume is the website's own seeded default rather than something they entered — a revenue figure derived from our starting value would be ours, not theirs. " +
  `Always in ${CATALOG_CURRENCY}, Vera's pricing currency, whatever currency the visitor's own figures are in.`

/**
 * ⚠️ A FUNCTION OF `plans`, so it is built at the call site inside a `useMemo` keyed on the
 * catalog — unlike `buildWebsiteColumns()`, which closes over nothing. The catalog arrives
 * asynchronously; until it does, `plans` is empty, no schedule resolves, and every cell
 * renders the dash. That is the correct intermediate state (the alternative, a skeleton in
 * one column of an otherwise-loaded grid, says "a value is coming" about rows where none
 * ever will) but it does mean the column POPULATES A MOMENT AFTER the rest of the row.
 */
export const buildPotentialRevenueColumn = (plans: SubscriptionPlan[]) => ({
  /*
    ⚠️ STILL `potential_revenue` after the rename to "Potential subscription ARR", and
    deliberately. The field id is the grid's own key: it is what the Layout panel's
    visibility state and any saved column order are persisted against, so renaming it would
    silently reset the layout of anyone who had hidden or reordered this column. It is
    never shown to a user — `headerName` is what appears in the header, the column menu and
    the CSV export, and that is what moved.
  */
  field: 'potential_revenue',
  headerName: COLUMN_LABEL,
  type: 'number' as const,
  width: HEADER_WIDTH + INFO_HEADER_ALLOWANCE,
  sortable: true,
  filterable: true,
  renderHeader: headerWithInfo(COLUMN_LABEL, HEADER_NOTE),
  /*
    MUI X v7 signature `(value, row, column, apiRef)`. There is no `row.potential_revenue`
    to read — the whole column is computed — so the row is the only input, and this is also
    what the CSV export and the sort/filter engines consult. `renderCell` formats what this
    returns and never recomputes it, so a sort cannot disagree with what is on screen.

    Returns `null`, not 0, for a row that cannot be priced: 0 is a revenue figure and would
    sort among the real ones as the cheapest lead on the page.
  */
  valueGetter: (_value: unknown, row: SignupEntry) =>
    row ? potentialSubscriptionRevenue(row, plans)?.total ?? null : null,
  renderCell: ({ row }: { row: SignupEntry }) => {
    const revenue = row ? potentialSubscriptionRevenue(row, plans) : null
    if (!revenue) {
      return (
        <Box sx={cellSx}>
          <Typography variant="body2" color="text.disabled">
            {EMPTY_VALUE}
          </Typography>
        </Box>
      )
    }
    return (
      <Tooltip title={breakdown(revenue)}>
        <Stack direction="row" spacing={2} alignItems="center" sx={cellSx}>
          <Typography variant="body2" noWrap>
            {money(revenue.total)}
          </Typography>
        </Stack>
      </Tooltip>
    )
  },
})
